import { PatientFormSchemaType } from '@pages/main/patient/types';
import {
  PATIENT_EDIT_HISTORY_SEARCH,
} from '@pages/main/patient/utils/patientEditHistoryUrl';
import { toOptionalNumber } from './usePatientEditPage.formValues';

export const BULK_HISTORY_NO_CHANGE_MESSAGE = '変更箇所が無い場合は、履歴作成できません。';

const BULK_HISTORY_PI_COMPARE_FIELDS = [
  'name',
  'name_kana',
  'relationship_id',
  'sex_id',
  'date_of_birth',
  'postal_code1',
  'postal_code2',
  'prefecture_id',
  'address1',
  'address2',
  'address3',
  'telephone',
  'address_name',
  'is_special_area_addition',
  'address_kana',
  'summary',
  'remarks',
  'is_exclusion',
  'is_non_output_telephone',
  'first_name',
  'last_name',
  'first_name_kana',
  'last_name_kana',
  'address3_check',
  'address2_hidden',
  'gaiji_check',
] as const;

const BULK_HISTORY_HIC_COMPARE_FIELDS = [
  'relationship_id',
  'insurance_type_id',
  'insurer_id',
  'insurer_number',
  'insurer_kind_name',
  'insurer_name',
  'patient_cost_rate',
  'patient_cost_id',
  'elderly_insurance_certificate_id',
  'card_mark',
  'card_number',
  'name',
  'name_kana',
  'date_of_birth',
  'sex_id',
  'postal_code1',
  'postal_code2',
  'prefecture_id',
  'address1',
  'address2',
  'address3',
  'telephone',
  'address_kana',
  'first_name',
  'last_name',
  'first_name_kana',
  'last_name_kana',
] as const;

const BULK_HISTORY_MA_COMPARE_FIELDS = [
  'insurer_type_id',
  'insurer_id',
  'medical_aid_number',
  'medical_aid_patient_number',
  'insurer_cost_rate',
  'is_partial_aid',
  'is_special',
  'is_nonoutput_rezept',
  'is_end_medical_aid',
] as const;

const normalizeForBulkHistoryNoChangeCompare = (value: unknown): unknown => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map(normalizeForBulkHistoryNoChangeCompare);
  }
  if (typeof value === 'object') {
    const source = value as Record<string, unknown>;
    return Object.keys(source)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizeForBulkHistoryNoChangeCompare(source[key]);
        return acc;
      }, {});
  }
  if (typeof value === 'string') {
    return value.trim();
  }

  return value;
};

const pickBulkHistoryCompareFields = (
  value: unknown,
  fields: readonly string[]
): Record<string, unknown> => {
  const source =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};

  return fields.reduce<Record<string, unknown>>((acc, key) => {
    acc[key] = normalizeForBulkHistoryNoChangeCompare(source[key]);
    return acc;
  }, {});
};

const bulkHistoryNoChangeSnapshot = (value: PatientFormSchemaType): Record<string, unknown> => ({
  patient_information: pickBulkHistoryCompareFields(
    value.patient_information,
    BULK_HISTORY_PI_COMPARE_FIELDS
  ),
  health_insurance_card: pickBulkHistoryCompareFields(
    value.health_insurance_card,
    BULK_HISTORY_HIC_COMPARE_FIELDS
  ),
  medical_aid: pickBulkHistoryCompareFields(value.medical_aid, BULK_HISTORY_MA_COMPARE_FIELDS),
});

export const isBulkHistoryDraftUnchanged = (
  current: PatientFormSchemaType,
  baseline: PatientFormSchemaType | null
): boolean => {
  if (!baseline) return false;

  return (
    JSON.stringify(bulkHistoryNoChangeSnapshot(current)) ===
    JSON.stringify(bulkHistoryNoChangeSnapshot(baseline))
  );
};

export const buildExistingHistorySearchParamsFromForm = (
  data: PatientFormSchemaType
): URLSearchParams | null => {
  const sp = new URLSearchParams();
  const patientInformationId = toOptionalNumber(data.patient_information?.id);
  if (patientInformationId) {
    sp.set(PATIENT_EDIT_HISTORY_SEARCH.patient_information_id, String(patientInformationId));
  }

  const healthInsuranceCardId = toOptionalNumber(data.health_insurance_card?.id);
  if (healthInsuranceCardId) {
    sp.set(PATIENT_EDIT_HISTORY_SEARCH.health_insurance_card_id, String(healthInsuranceCardId));
  }

  const medicalAidId = toOptionalNumber(data.medical_aid?.id);
  if (medicalAidId) {
    sp.set(PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id, String(medicalAidId));
  }

  return sp.toString() ? sp : null;
};

/**
 * A2「新しい情報を入力する」draft: form must have no persisted PI row id.
 * Spreading `id: undefined` into reset() often leaves the previous number in RHF state.
 */
export const patientInformationValuesForNewHistoryDraft = (
  pi: PatientFormSchemaType['patient_information']
): PatientFormSchemaType['patient_information'] => {
  const { id: _removed, ...rest } = pi;
  return rest as PatientFormSchemaType['patient_information'];
};

export const healthInsuranceCardValuesForNewHistoryDraft = (
  card: PatientFormSchemaType['health_insurance_card']
): PatientFormSchemaType['health_insurance_card'] => {
  const { id: _removed, ...rest } = card;
  return rest as PatientFormSchemaType['health_insurance_card'];
};
